import {
	Alert,
	Box,
	Button,
	Inline,
	Icons,
	Stack,
	Text,
	Spinner,
} from "@sembark-travel/ui-base"
import {
	NavigationPromptDialog,
	searchToQuery,
	useNavigationPrompt,
} from "@sembark-travel/ui-router"
import {
	formatDate,
	isSame,
	addUnit,
	localOrUtcTimestampToLocalDate,
} from "@sembark-travel/datetime-utils"
import { useXHR, xhrConfig } from "@sembark-travel/xhr"
import {
	useCallback,
	useEffect,
	useMemo,
	useReducer,
	useRef,
	useState,
} from "react"
import {
	IQuoteCab,
	IQuoteHotel,
	IQuoteHotelExtras,
	IQuoteFlight,
	IQuoteTransportExtras,
	ITrip,
	ITripQuote,
	IQuoteTravelActivity,
	IQuoteHotelParams,
} from "./store"
import { useLocation, useNavigate } from "./../router-utils"
import { ItineraryStreamPreview } from "./ItineraryStreamPreview"
import { addKeyToFieldArrayItem } from "@sembark-travel/ui-form"
import { ICabType } from "../CabTypes"
import { ITransportService } from "../TransportServices"
import { TValidDateswiseTransportsAndActivitiesInputFieldValue } from "../TransportServicePrices/utils"
import { TTravelActivity, TTravelActivityTicketType } from "../TravelActivities"
import { NewQuoteForm } from "./NewQuote"
import config from "../config"

export default function AiItineraryNewQuote({
	trip,
	onStreamingChange,
}: {
	trip: ITrip
	onStreamingChange: (isStreaming: boolean) => void
}) {
	const { blocker, disablePrompt, enablePrompt } = useNavigationPrompt(true)
	const location = useLocation()
	const navigate = useNavigate()
	const locationSearch = location.search
	const locationQuery = useMemo(() => {
		const locationQuery = searchToQuery<{
			generationId?: string
			offset?: string
		}>(locationSearch)
		const generationId = locationQuery.generationId || undefined
		const offset = locationQuery.offset || undefined
		return {
			generationId,
			offset,
		}
	}, [locationSearch])
	const { generationId, offset } = locationQuery

	const defaultBasicDetailsRef = useRef({
		startDate: localOrUtcTimestampToLocalDate(
			trip.start_date_local,
			trip.start_date
		),
		days: trip.days,
		adults: trip.no_of_adults,
		children: trip.children || [],
	})
	const { startDate, days, adults, children } = defaultBasicDetailsRef.current

	const onSuccess = useCallback(
		(quote: ITripQuote) => {
			if (navigate) {
				disablePrompt()
				navigate("/trips/:tripId/quotes/:quoteId", {
					params: { tripId: String(trip.id), quoteId: String(quote.id) },
					replace: true,
				})
			}
		},
		[navigate, trip, disablePrompt]
	)

	const onCancel = useCallback(() => {
		disablePrompt()
		navigate(-1)
	}, [navigate, disablePrompt])

	const commonProps = {
		startDate,
		days,
		tripId: trip.id,
		adults,
		children,
		navigate,
		trip,
		baseStartDate: localOrUtcTimestampToLocalDate(
			trip.start_date_local,
			trip.start_date
		),
		baseNoOfAdults: trip.no_of_adults,
		baseChildren: trip.children,
		onCancel,
		onSuccess,
		isEditing: false,
		afterConfirmedQuote: false,
		isUsingExistingQuote: false,
		hasNotEditPermission: true,
	}

	const {
		streamQuote,
		isLiveStreaming,
		streamError,
		onRetry,
		streamLabel,
		onCancelStream,
		completedSections,
		isHistoricalFetching,
	} = useItineraryStream({
		generationId,
		tripId: trip.id,
		trip,
		startDate,
		initialOffset: offset || "-1",
	})
	useEffect(
		() => onStreamingChange(isLiveStreaming),
		[isLiveStreaming, onStreamingChange]
	)
	const retryHandler = useCallback(() => {
		enablePrompt()
		onRetry()
	}, [onRetry, enablePrompt])

	if (streamError) {
		return (
			<ErrorState
				disablePrompt={disablePrompt}
				message={streamError}
				onRetry={retryHandler}
				onBack={onCancel}
			/>
		)
	}

	if (isLiveStreaming && !isHistoricalFetching) {
		return (
			<>
				<ItineraryStreamPreview
					streamQuote={streamQuote}
					startDate={startDate}
					days={days}
					trip={trip}
					streamLabel={streamLabel}
					completedSections={completedSections}
				/>
				<NavigationPromptDialog onLeave={onCancelStream} blocker={blocker} />
			</>
		)
	}
	if (!streamQuote || isHistoricalFetching) {
		return <Spinner padding="4" alignCenter />
	}

	return (
		<>
			<NewQuoteForm
				generationId={generationId ? String(generationId) : undefined}
				quote={streamQuote}
				isAIItinerarybuilder
				complimentaryCount={0}
				{...commonProps}
			/>
			<NavigationPromptDialog onLeave={onCancelStream} blocker={blocker} />
		</>
	)
}

interface IStreamHotelData {
	hotel?: IQuoteHotelParams[number]["hotel"]
	stream_data?: {
		hotel_room_id?: number | string
		meal_plan_id?: number | string
		check_in_day?: number
		nights?: number
		pax?: number
		aweb?: number
		cweb?: number
		cnb?: number
		num_rooms?: number
	}
	data?: {
		hotel?: IStreamHotelData["hotel"]
		stream_data?: IStreamHotelData["stream_data"]
	}
}

interface IStreamTransportData {
	type: "transport"
	day: number
	data: {
		transport_service: ITransportService
		cab_types: ICabType[]
		transport_data_from_stream: {
			cabs: {
				number_of_cabs: number
				transport_cab_id: number
			}[]
		}
		day: number
	}
}

interface IStreamActivityData {
	type: "activity"
	day: number
	data: {
		ticket_type: TTravelActivityTicketType & { activity: TTravelActivity }
		travel_activity_data_from_stream: {
			start_time?: string
			notes?: string
		}
		day: number
	}
}

function mapStreamHotelToQuoteHotel(
	data: IStreamHotelData,
	tripStartDate: Date
): IQuoteHotelParams[number] | null {
	if (!data) return null

	let hotel = data.hotel
	let stream_data = data.stream_data

	if (!hotel && data.data?.hotel) {
		hotel = data.data.hotel
		stream_data = data.data.stream_data
	}

	if (!hotel) return null

	const roomType =
		hotel?.room_types?.find(
			(r) => String(r.id) === String(stream_data?.hotel_room_id)
		) || hotel?.room_types?.[0]

	const mealPlan =
		hotel?.meal_plans?.find(
			(m) => String(m.id) === String(stream_data?.meal_plan_id)
		) || hotel?.meal_plans?.[0]

	const checkInDay = stream_data?.check_in_day || 1
	const nights = stream_data?.nights || 1

	const checkInDate = addUnit(tripStartDate, checkInDay - 1, "days")
	const dates: Date[] = []
	for (let i = 0; i < nights; i++) {
		dates.push(addUnit(checkInDate, i, "days") as Date)
	}

	return {
		hotel: hotel,
		dates: dates,
		no_of_nights: nights,
		room_type: roomType,
		meal_plan: mealPlan,
		persons_per_room: stream_data?.pax || 1,
		no_of_rooms: stream_data?.num_rooms || 1,
		adults_with_extra_bed: stream_data?.aweb || 0,
		children_with_extra_bed: stream_data?.cweb || 0,
		children_without_extra_bed: stream_data?.cnb || 0,
		date_wise_prices: [],
		notes: [],
		comments: "",
	}
}

function mapStreamActivityToQuoteService(
	streamData: IStreamActivityData,
	tripStartDate: Date
): TValidDateswiseTransportsAndActivitiesInputFieldValue[number] | null {
	if (!streamData?.data?.ticket_type) return null

	const { ticket_type, travel_activity_data_from_stream, day } = streamData.data
	const checkInDate = addUnit(tripStartDate, (day || 1) - 1, "days")

	const activity = ticket_type.activity
	const duration = ticket_type.durations?.[0]

	return {
		__id: ++_streamCounter,
		dates: [checkInDate as Date],
		no_of_days: 1,
		services: [
			{
				__id: ++_streamCounter,
				type: "travel_activity",
				service: {
					dates: [checkInDate as Date],
					activity: {
						...activity,
						ticket_types: [ticket_type],
						ticket_tourist_configurations:
							activity.ticket_tourist_configurations,
					},
					ticket_type: ticket_type,
					duration: duration
						? { iso: duration.iso, name: duration.name }
						: undefined,
					activity_slots: duration?.slots,
					slot: travel_activity_data_from_stream?.start_time || undefined,
					comments: travel_activity_data_from_stream?.notes || "",
					ticket_tourist_configurations: (
						activity.ticket_tourist_configurations || []
					).map((c) =>
						addKeyToFieldArrayItem({
							configuration: c,
							quantity: c.quantity,
							fetching_prices: false,
							date_wise_prices: [],
						})
					),
				},
			},
		],
	}
}

let _streamCounter = Date.now()

function mapStreamTransportToQuoteService(
	streamData: IStreamTransportData,
	tripStartDate: Date
): TValidDateswiseTransportsAndActivitiesInputFieldValue[number] | null {
	if (!streamData || !streamData.data || !streamData.data.transport_service)
		return null

	const { transport_service, cab_types, transport_data_from_stream, day } =
		streamData.data
	const checkInDate = addUnit(tripStartDate, (day || 1) - 1, "days")

	return {
		__id: ++_streamCounter,
		dates: [checkInDate as Date],
		no_of_days: 1,
		services: [
			{
				__id: ++_streamCounter,
				type: "transport_service",
				service: {
					transport_service: transport_service,
					transport_service_location: transport_service.locations,
					comments: "",
					cabs: cab_types.map((cab_type) => {
						const cab = transport_data_from_stream?.cabs.find(
							(c) => c.transport_cab_id === cab_type.id
						)
						return {
							__id: ++_streamCounter,
							no_of_cabs: cab?.number_of_cabs || 1,
							cab_type: cab_type,
							fetching_prices: 0,
						}
					}),
				},
			},
		],
	}
}

interface IStreamSectionCompletedData {
	section: "hotels" | "day"
	day?: number
}

type StreamFetchState = {
	isHistoricalFetching: boolean
	isLiveStreaming: boolean
	streamError: string | null
	streamLabel: string | null
	retryKey: number
}

type StreamFetchAction =
	| { type: "START_CONNECT" }
	| { type: "PERSISTED_DATA_NOT_FOUND" }
	| { type: "STREAM_DONE" }
	| { type: "STREAM_ERROR"; payload?: string }
	| { type: "SET_STREAM_LABEL"; payload: string | null }
	| { type: "RETRY" }

const initialStreamFetchState: StreamFetchState = {
	isHistoricalFetching: true,
	isLiveStreaming: true,
	streamError: null,
	streamLabel: null,
	retryKey: 0,
}

function streamFetchReducer(
	state: StreamFetchState,
	action: StreamFetchAction
): StreamFetchState {
	switch (action.type) {
		case "START_CONNECT":
			return {
				...state,
				isHistoricalFetching: true,
				isLiveStreaming: true,
				streamError: null,
			}
		case "PERSISTED_DATA_NOT_FOUND":
			return {
				...state,
				isHistoricalFetching: false,
				isLiveStreaming: true,
			}
		case "STREAM_DONE":
			return {
				...state,
				isHistoricalFetching: false,
				isLiveStreaming: false,
				streamError: null,
			}
		case "STREAM_ERROR":
			return {
				...state,
				isLiveStreaming: false,
				streamError:
					action.payload ||
					"Unable to connect to itinerary stream. Please refresh or try again later.",
			}
		case "SET_STREAM_LABEL":
			return {
				...state,
				streamLabel: action.payload,
			}
		case "RETRY":
			return {
				...state,
				streamError: null,
				streamLabel: null,
				isLiveStreaming: false,
				retryKey: state.retryKey + 1,
			}
		default:
			return state
	}
}

function useItineraryStream({
	generationId,
	tripId,
	trip,
	startDate,
	initialOffset,
}: {
	generationId: string | undefined
	tripId: number | string
	trip?: ITrip
	startDate: Date
	initialOffset: string
}) {
	const streamNextOffset = useRef(initialOffset)
	const quoteIdRef = useRef(Date.now())
	const collectedHotels = useRef<
		Array<{ variant?: string; hotels: IQuoteHotelParams }>
	>([])
	const collectedServices =
		useRef<TValidDateswiseTransportsAndActivitiesInputFieldValue>([])

	const [completedSections, setCompletedSections] = useState<
		IStreamSectionCompletedData[]
	>([])
	const [accumulatedHotels, setAccumulatedHotels] = useState<
		Array<{ variant?: string; hotels: IQuoteHotelParams }>
	>([])
	const [accumulatedServices, setAccumulatedServices] =
		useState<TValidDateswiseTransportsAndActivitiesInputFieldValue>([])
	const [
		{
			isHistoricalFetching,
			isLiveStreaming,
			streamError,
			streamLabel,
			retryKey,
		},
		dispatchStreamFetch,
	] = useReducer(streamFetchReducer, initialStreamFetchState)
	const xhr = useXHR()

	const onRetry = useCallback(() => {
		dispatchStreamFetch({ type: "RETRY" })
	}, [])

	useEffect(() => {
		if (
			!generationId ||
			!tripId ||
			!(startDate instanceof Date) ||
			isNaN(startDate.getTime())
		) {
			return
		}
		streamNextOffset.current = initialOffset
		collectedHotels.current = []
		collectedServices.current = []
		setCompletedSections([])
		dispatchStreamFetch({ type: "START_CONNECT" })

		let eventSource: EventSource | null = null

		const safeParse = <T,>(value: string): T | null => {
			try {
				return JSON.parse(value)
			} catch (err) {
				console.error("Invalid SSE payload:", err)
				return null
			}
		}

		const mergeServices = (
			mappedItems: TValidDateswiseTransportsAndActivitiesInputFieldValue
		) => {
			mappedItems.forEach((newItem) => {
				const existingGroupIndex = collectedServices.current.findIndex(
					(item) =>
						item.dates.length &&
						newItem.dates.length &&
						isSame(item.dates[0], newItem.dates[0], "day")
				)
				if (existingGroupIndex !== -1) {
					const existingGroup = collectedServices.current[existingGroupIndex]
					const updatedServices = [...existingGroup.services]

					newItem.services.forEach((newS) => {
						if (newS.type === "travel_activity") {
							const existingServiceItem = updatedServices.find(
								(s) =>
									s.type === "travel_activity" &&
									s.service?.activity?.id === newS.service?.activity?.id &&
									s.service?.ticket_type?.id === newS.service?.ticket_type?.id
							)
							if (
								existingServiceItem &&
								existingServiceItem.type === "travel_activity"
							) {
								// Merge comments/notes
								const existingComments =
									existingServiceItem.service.comments || ""
								const newComments = newS.service.comments || ""
								if (newComments && !existingComments.includes(newComments)) {
									existingServiceItem.service.comments = existingComments
										? `${existingComments}\n${newComments}`
										: newComments
								}

								// Merge ticket_tourist_configurations
								const mergedConfigs = [
									...(existingServiceItem.service
										.ticket_tourist_configurations || []),
								]
								const newConfigs =
									newS.service.ticket_tourist_configurations || []

								newConfigs.forEach((newConf) => {
									const existingConfIndex = mergedConfigs.findIndex(
										(c) => c.configuration?.id === newConf.configuration?.id
									)
									if (existingConfIndex !== -1) {
										const existingConf = mergedConfigs[existingConfIndex]
										mergedConfigs[existingConfIndex] = {
											...existingConf,
											quantity:
												(existingConf.quantity || 0) + (newConf.quantity || 0),
										}
									} else {
										mergedConfigs.push(newConf)
									}
								})
								existingServiceItem.service.ticket_tourist_configurations =
									mergedConfigs
							} else {
								updatedServices.push(newS)
							}
						} else {
							// For transport services, just push
							updatedServices.push(newS)
						}
					})

					collectedServices.current[existingGroupIndex] = {
						...existingGroup,
						services: updatedServices,
					}
				} else {
					collectedServices.current.push(newItem)
				}
			})
		}
		function onError(errorMeg?: string) {
			eventSource?.close()
			setAccumulatedHotels([...collectedHotels.current])
			setAccumulatedServices([...collectedServices.current])
			dispatchStreamFetch({ type: "STREAM_ERROR", payload: errorMeg })
		}

		const connect = () => {
			if (eventSource?.readyState === EventSource.OPEN) {
				return
			}
			eventSource?.close()
			const params = new URLSearchParams({
				trip_id: String(tripId),
				generation_id: generationId,
				offset: streamNextOffset.current,
				app_version: xhrConfig.appVersion,
			})

			eventSource = new EventSource(
				`${config.apiBaseUrl}/itinerary-builder/stream?${params.toString()}`,
				{ withCredentials: true }
			)

			eventSource.onopen = () => {
				dispatchStreamFetch({ type: "START_CONNECT" })
			}

			eventSource.onerror = () => onError()

			// // --- Event Listeners ---
			eventSource.addEventListener("error", (event) => {
				const data = safeParse<{
					type: string
					message: string
				}>((event as MessageEvent).data)

				onError(data?.message)
			})
			eventSource.addEventListener("label", (event) => {
				const payload = safeParse<{
					type: string
					data: string
				}>(event.data)

				if (payload?.type === "label" && payload.data) {
					dispatchStreamFetch({
						type: "SET_STREAM_LABEL",
						payload: payload.data,
					})
				}
			})

			eventSource.addEventListener("hotels", (event) => {
				const payload = safeParse<{
					type: string
					variant?: string
					data: IStreamHotelData[]
				}>(event.data)

				if (
					!payload ||
					payload.type !== "hotels" ||
					!Array.isArray(payload.data)
				)
					return

				const hotels: IQuoteHotelParams = []
				payload.data.forEach((hotelData: IStreamHotelData) => {
					const mappedHotel = mapStreamHotelToQuoteHotel(hotelData, startDate)
					if (mappedHotel) {
						hotels.push(mappedHotel)
					}
				})

				if (hotels.length > 0) {
					collectedHotels.current.push({
						variant: payload.variant,
						hotels: hotels,
					})
					setAccumulatedHotels([...collectedHotels.current])
				}
			})

			eventSource.addEventListener("transport", (event) => {
				const transportData = safeParse<IStreamTransportData>(event.data)
				if (!transportData) return
				const mapped: NonNullable<
					ReturnType<typeof mapStreamTransportToQuoteService>
				>[] = []
				if (
					transportData &&
					transportData.type === "transport" &&
					transportData.data
				) {
					const mappedItem = mapStreamTransportToQuoteService(
						transportData,
						startDate
					)
					if (mappedItem) {
						mapped.push(mappedItem)
					}
				}

				if (mapped.length > 0) {
					mergeServices(mapped)
					setAccumulatedServices([...collectedServices.current])
				}
			})

			eventSource.addEventListener("activity", (event) => {
				const activityData = safeParse<IStreamActivityData>(event.data)
				if (!activityData) return

				const mapped: NonNullable<
					ReturnType<typeof mapStreamActivityToQuoteService>
				>[] = []
				if (
					activityData &&
					activityData.type === "activity" &&
					activityData.data
				) {
					const mappedItem = mapStreamActivityToQuoteService(
						activityData,
						startDate
					)
					if (mappedItem) {
						mapped.push(mappedItem)
					}
				}

				if (mapped.length > 0) {
					mergeServices(mapped)
					setAccumulatedServices([...collectedServices.current])
				}
			})

			const updateOffset = (event: MessageEvent) => {
				dispatchStreamFetch({ type: "PERSISTED_DATA_NOT_FOUND" })
				const payload = safeParse<{ data?: { streamNextOffset?: string } }>(
					event.data
				)
				if (payload?.data?.streamNextOffset != null) {
					streamNextOffset.current = payload.data.streamNextOffset
				}
			}

			eventSource.addEventListener("section_completed", (event) => {
				const payload = safeParse<{ data: IStreamSectionCompletedData }>(
					event.data
				)
				if (payload?.data) {
					setCompletedSections((prev) => [...prev, payload.data])
				}
			})

			eventSource.addEventListener("control", updateOffset)

			eventSource.addEventListener("done", () => {
				streamNextOffset.current = initialOffset
				setAccumulatedHotels([...collectedHotels.current])
				setAccumulatedServices([...collectedServices.current])
				dispatchStreamFetch({ type: "STREAM_DONE" })
				eventSource?.close()
			})
		}

		connect()

		return () => {
			eventSource?.close()
		}
	}, [generationId, tripId, startDate, retryKey, initialOffset])

	const streamQuote = useMemo<ITripQuote | undefined>(() => {
		type IStreamQuoteOption = Pick<
			ITripQuote,
			"id" | "trip_id" | "quote_id" | "currency" | "pricing_strategy" | "name"
		> & {
			quote: {
				hotels: Array<
					Partial<IQuoteHotel> & { __id: number; no_of_nights: number }
				>
				cabs: Array<Partial<IQuoteCab>>
				travel_activities: Array<Partial<IQuoteTravelActivity>>
				hotel_extras: Array<Partial<IQuoteHotelExtras>>
				transport_extras: Array<Partial<IQuoteTransportExtras>>
				other_extras: Array<Partial<IQuoteTransportExtras>>
				flights: Array<Partial<IQuoteFlight>>
			}
		}

		const emptyQuote: IStreamQuoteOption["quote"] = {
			hotels: [],
			cabs: [],
			travel_activities: [],
			hotel_extras: [],
			transport_extras: [],
			other_extras: [],
			flights: [],
		}

		const defaultCurrency = trip?.destinations?.[0]?.currency || "INR"

		const options: IStreamQuoteOption[] = accumulatedHotels.length
			? accumulatedHotels.map((ph, index) => ({
					id: quoteIdRef.current + index,
					trip_id: tripId as number,
					quote_id: quoteIdRef.current,
					currency: defaultCurrency,
					pricing_strategy: "overall_v2" as const,
					name: ph.variant || `Option ${index + 1}`,
					quote: {
						...emptyQuote,
						hotels: (ph.hotels || []).flatMap((h) =>
							(h.dates || []).map((d, dIdx) => {
								const stableId = d.getTime() + (h.hotel?.id || 0) + index
								return {
									__id: stableId,
									id: stableId,
									quote_id: quoteIdRef.current,
									date: d.toISOString(),
									date_local: undefined,
									hotel: h.hotel,
									hotel_id: h.hotel?.id,
									room_type: h.room_type,
									room_type_id: h.room_type?.id,
									meal_plan: h.meal_plan,
									meal_plan_id: h.meal_plan?.id,
									no_of_rooms: h.no_of_rooms,
									no_of_nights: h.no_of_nights || 1,
									persons_per_room: h.persons_per_room,
									adults_with_extra_bed: h.adults_with_extra_bed,
									children_with_extra_bed: h.children_with_extra_bed,
									children_without_extra_bed: h.children_without_extra_bed,
									currency: defaultCurrency,
									calculated_price: h.date_wise_prices?.[dIdx]?.price || 0,
									given_price: h.date_wise_prices?.[dIdx]?.given_price || 0,
									comments: h.comments || "",
									similar_hotel_options: [],
								}
							})
						),
					},
				}))
			: [
					{
						id: quoteIdRef.current,
						trip_id: tripId as number,
						quote_id: quoteIdRef.current,
						currency: defaultCurrency,
						pricing_strategy: "overall_v2" as const,
						name: "Option 1",
						quote: { ...emptyQuote },
					},
				]

		accumulatedServices.forEach((group, index) => {
			const groupId = index + 1
			const dateStr = group.dates[0]
				? formatDate(group.dates[0], "YYYY-MM-DD")
				: ""

			group.services.forEach((item, sIndex) => {
				if (item.type === "transport_service") {
					const cabsArray = item.service.cabs || []
					cabsArray.forEach((cabItem) => {
						const cabCurrency = defaultCurrency
						options[0].quote.cabs.push({
							id: cabItem.__id,
							quote_id: quoteIdRef.current,
							date: dateStr,
							group_id: groupId,
							sort_order: sIndex + 1,
							transport_service: item.service.transport_service,
							transport_service_id: item.service.transport_service?.id,
							cab_type: cabItem.cab_type,
							cab_type_id: cabItem.cab_type?.id,
							no_of_cabs: cabItem.no_of_cabs || 1,
							currency: cabCurrency,
							calculated_price: 0,
							given_price: 0,
							booked_price: 0,
							comments: item.service.comments || "",
						})
					})
				} else if (item.type === "travel_activity") {
					options[0].quote.travel_activities.push({
						id: item.__id,
						quote_id: quoteIdRef.current,
						date: dateStr,
						group_id: groupId,
						sort_order: sIndex + 1,
						activity: item.service.activity,
						ticket_type: item.service.ticket_type,
						duration: item.service.duration?.iso,
						slot: item.service.slot,
						ticket_tourist_configurations:
							item.service.ticket_tourist_configurations?.map((t) => ({
								configuration: t.configuration,
								quantity: t.quantity || 1,
								currency: defaultCurrency,
								calculated_price: 0,
								given_price: 0,
								complementary: 0,
							})) || [],
					})
				}
			})
		})

		return {
			...options[0],
			options: options.slice(1),
			can_share_options: true,
		} as unknown as ITripQuote
	}, [accumulatedHotels, accumulatedServices, tripId, trip])

	const onCancelStream = useCallback(async () => {
		if (isLiveStreaming && generationId) {
			await xhr.get("/itinerary-builder/abort-stream", {
				params: {
					generation_id: generationId,
				},
			})
		}
	}, [generationId, isLiveStreaming, xhr])

	return {
		streamQuote,
		completedSections,
		accumulatedHotels,
		setAccumulatedHotels,
		accumulatedServices,
		setAccumulatedServices,
		streamError,
		isLiveStreaming,
		isHistoricalFetching,
		onRetry,
		streamLabel,
		onCancelStream,
	}
}

interface ErrorStateProps {
	message: string
	onRetry?: () => void
	onBack?: () => void
	disablePrompt: () => void
}
function ErrorState({
	message,
	onRetry,
	onBack,
	disablePrompt,
}: ErrorStateProps) {
	useEffect(() => {
		disablePrompt()
	}, [disablePrompt])
	return (
		<Box padding="4">
			<Alert status="error">
				<Stack gap="2">
					<Text>{message}</Text>

					<Inline gap="2">
						{onBack && (
							<Button onClick={onBack} size="sm">
								<Icons.ArrowLeft /> Back
							</Button>
						)}
						{onRetry && (
							<Button onClick={onRetry} size="sm" level="secondary">
								<Icons.Refresh /> Retry
							</Button>
						)}
					</Inline>
				</Stack>
			</Alert>
		</Box>
	)
}
